import styled from "styled-components";
import { Link } from "react-router-dom";

import FooterContactItem from "./FooterContactItem";
import { PATH_HOME, PATH_CART, PATH_PRIVACY_POLICY } from "@routes";
import { breakpoint, theme } from "@utilities";
import { selectCmsFooterMenu, useAppSelector } from "@redux";

export const FooterComponent = () => {
    const cmsFooterMenu = useAppSelector(selectCmsFooterMenu);
    const socialIcon = [
        {
            icon: "/images/website/footer/fb.png",
            link: theme.socialLinks.facebook,
        },
        {
            icon: "/images/website/footer/insta.png",
            link: theme.socialLinks.instagram,
        },
        ...(theme.socialLinks.linkedin
            ? [
                  {
                      icon: "/images/website/footer/akar-icons_linkedin-fill.png",
                      link: theme.socialLinks.linkedin,
                  },
              ]
            : []),
    ];

    const fallbackMenu = [
        {
            title: "Home",
            link: PATH_HOME,
        },
        {
            title: "Cart",
            link: PATH_CART,
        },
        {
            title: "Privacy Policy",
            link: PATH_PRIVACY_POLICY,
        },
    ];

    // Prefer backend-driven menu (per flavor) when available, else fallback.
    const menu =
        cmsFooterMenu && cmsFooterMenu.items.length > 0
            ? cmsFooterMenu.items.map((item) => ({ title: item.label, link: item.href }))
            : fallbackMenu;

    const contactList = [
        {
            img: "/images/website/footer/fi_map-pin.png",
            title: theme.contactLocation,
        },
        {
            img: "/images/website/footer/fi_mail.png",
            title: theme.contactEmail,
        },
    ];

    return (
        <StyledFooterWebsite>
            <div className="wrapper">
                <div className="follow">
                    <Link to={PATH_HOME} onClick={() => window.scrollTo(0, 0)}>
                        <img
                            src="/images/logos/Primary_logo_color.svg"
                            alt="logo"
                            className="logo"
                        />
                    </Link>
                    <div className="social">
                        {socialIcon.map((icon, index) => {
                            return (
                                <a className="icon" key={index} href={icon.link}>
                                    <img src={icon.icon} alt="icon" />
                                </a>
                            );
                        })}
                        {theme.socialLinks.tiktok && (
                            <a className="icon" href={theme.socialLinks.tiktok}>
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                </svg>
                            </a>
                        )}
                    </div>
                </div>
                <div className="section travel">
                    <p className="title">{theme.title}</p>
                    <div>
                        {menu.map((item, index) => {
                            return (
                                <Link key={index} to={item.link}>
                                    <p className="menuItem">{item.title}</p>
                                </Link>
                            );
                        })}
                    </div>
                </div>
                <div className="section contact">
                    <p className="title">Contact</p>
                    <div>
                        {contactList.map((item, index) => (
                            <FooterContactItem key={index} img={item.img} title={item.title} />
                        ))}
                    </div>
                </div>
            </div>
            <p className="copyright">{theme.copyright}</p>
        </StyledFooterWebsite>
    );
};

const StyledFooterWebsite = styled.div`
    width: 100%;
    background-color: ${(props) => props.theme.colors.gray_6};
    display: block;

    ${breakpoint.breakLarge`
        padding-top: 20px;
    `}

    .wrapper {
        width: ${(props) => props.theme.widths.wide_700};
        margin: auto;
        display: flex;
        justify-content: space-between;

        ${breakpoint.breakIpadPro`
            width: ${(props) => props.theme.widths.wide_900};
        `}

        ${breakpoint.breakLarge`
            width: ${(props) => props.theme.widths.wide_1110};
        `}

        div {
            list-style: none;
        }

        .title {
            color: ${(props) => props.theme.colors.black};
        }

        .follow {
            margin-top: 50px;
            /* Logo and icons share a centre line, which is the only way the icons
               can be both centred and under the logo — the column is wider than
               the 150-240px mark itself. */
            display: flex;
            flex-direction: column;
            align-items: center;

            .logo {
                width: 150px;

                ${breakpoint.breakIpadPro`
                    width: 210px;
                `}

                ${breakpoint.breakLarge`
                    width: 240px;
                `}
            }
            .title {
                font-size: 13px;
                font-weight: 700;
                margin-bottom: 15px;

                ${breakpoint.breakIpadPro`
                    font-size: 16px;
                `}

                ${breakpoint.breakLarge`
                    font-size: 18px;
                    margin-bottom: 20px;
                `}
            }

            .social {
                display: flex;
                align-items: center;
                justify-content: center;
                /* Clear of the logo above, which they were sitting flush against. */
                margin-top: 20px;

                /* The icons are white PNGs, so there is nothing to recolour by
                   CSS — brightness(0) maps every channel to black while leaving
                   the transparency alone, which is what makes them visible on the
                   grey footer. The TikTok glyph is inline SVG and sets its fill
                   directly. */
                img {
                    filter: brightness(0);
                }

                svg path {
                    fill: ${(props) => props.theme.colors.black};
                }

                .icon {
                    margin-right: 15px;

                    /* Otherwise the trailing gap pulls the row off centre. */
                    &:last-child {
                        margin-right: 0;
                    }

                    ${breakpoint.breakIpadPro`
                        margin-right: 20px;
                    `}

                    ${breakpoint.breakLarge`
                        margin-right: 30px;
                    `}
                    
                    img {
                        width: 15px;

                        ${breakpoint.breakIpadPro`
                            width: 18px;
                        `}

                        ${breakpoint.breakLarge`
                            width: 20px;
                        `}
                    }
                }
            }
        }

        .section {
            margin-top: 50px;

            .title {
                font-size: 12px;
                font-weight: 700;
                margin-bottom: 20px;

                ${breakpoint.breakIpadPro`
                    font-size: 14px;
                `}

                ${breakpoint.breakLarge`
                    font-size: 18px;
                `}
            }
            .menuItem {
                color: ${(props) => props.theme.colors.black};
                font-size: 10px;
                font-weight: 400;
                margin-bottom: 20px;

                ${breakpoint.breakIpadPro`
                    font-size: 12px;
                    margin-bottom: 20px;
                `}

                ${breakpoint.breakLarge`
                    font-size: 16px;
                `}
            }
        }
    }

    .copyright {
        background-color: ${(props) => props.theme.colors.main};
        color: ${(props) => props.theme.colors.button_text};
        padding: 1em 0;
        text-align: center;
        font-size: 10px;
        margin-top: 25px;
        font-weight: 400;

        ${breakpoint.breakIpadPro`
            font-size: 10px;
        `}

        ${breakpoint.breakLarge`
            margin-top: 65px;
            font-size: 12px;
        `}
    }
`;
